import { useMemo, useState } from 'react';
import {
  concretizePath,
  documentV3Schema,
  isBindV3,
  resolvePath,
  type BindV3,
  type DocumentV3,
  type FieldSpec,
  type PageV3,
  type StyleOverrides,
} from '@docgen/schema';
import { BLOCKS_BY_TYPE, type StyleFieldKey } from '@docgen/blocks';
import { Check, ChevronDown, ChevronUp, Copy, Link2, Link2Off, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { NativeSelect } from '../ui/select';
import { Textarea } from '../ui/textarea';
import {
  blockAncestry,
  concreteBindPath,
  duplicateBlock,
  duplicatePage,
  findBlock,
  removeBlock,
  removePage,
  repeatFramesFor,
  setBlockProp,
  setBlockStyle,
  setDocData,
  updateBlock,
  updatePage,
} from '../lib/doc-ops';
import { cn } from '../lib/utils';
import { collectArrayPaths, collectDataPaths, BindEditor, PropControl } from './fields';
import type { Selection } from './types';

export interface InspectorProps {
  doc: DocumentV3;
  selection: Selection;
  onChange: (next: DocumentV3, coalesceKey?: string) => void;
  onSelect: (selection: Selection) => void;
  /** Opens the Pricing panel — offered when a repeat's list is pricing-managed. */
  onOpenPricing?: () => void;
}

/** Lists whose items are rebuilt from pricing on every save; edit them in the Pricing panel, not as data. */
function isPricingManaged(over: string): boolean {
  return over === 'programs' || over === 'pricing.options' || over.startsWith('pricing.');
}

/** A short label for a repeat item: its name/title/first text field, else "Item N". */
function repeatItemLabel(item: unknown, index: number): string {
  if (item !== null && typeof item === 'object' && !Array.isArray(item)) {
    const obj = item as Record<string, unknown>;
    for (const key of ['name', 'title', 'label', 'heading', 'q', 'n']) {
      const value = obj[key];
      if (typeof value === 'string' && value.trim() !== '') return value;
    }
    return `Item ${index + 1}`;
  }
  const text = String(item ?? '');
  if (text.trim() === '') return `Item ${index + 1}`;
  return text.length > 40 ? `${text.slice(0, 40)}…` : text;
}

/** A new item shaped like the list's existing objects (fields blanked), or an empty string. */
function blankRepeatItem(items: readonly unknown[]): unknown {
  const template = items.find((x) => x !== null && typeof x === 'object' && !Array.isArray(x)) as
    | Record<string, unknown>
    | undefined;
  if (!template) return '';
  return Object.fromEntries(
    Object.keys(template).map((key) => {
      const sample = template[key];
      return [key, typeof sample === 'number' ? 0 : typeof sample === 'boolean' ? false : Array.isArray(sample) ? [] : ''];
    }),
  );
}

/**
 * Manages the items of a page's repeat list right in the inspector: add, remove
 * and reorder the data behind the repeat. Pricing-managed lists (programs,
 * pricing.options) are read-only here and point to the Pricing panel, since the
 * projection rebuilds them on save.
 */
function RepeatItemsEditor({
  doc,
  over,
  onChange,
  onOpenPricing,
}: {
  doc: DocumentV3;
  over: string;
  onChange: InspectorProps['onChange'];
  onOpenPricing?: (() => void) | undefined;
}) {
  const raw = resolvePath(doc.data, over);
  const items = Array.isArray(raw) ? raw : [];
  const setItems = (next: unknown[]) => onChange(setDocData(doc, over, next), `repeat-items:${over}`);

  if (isPricingManaged(over)) {
    return (
      <div className="space-y-1.5 border-t pt-3">
        <Label>Items (from pricing)</Label>
        <p className="text-[11px] text-muted-foreground">
          These pages are built from the proposal&apos;s pricing and rebuilt on save. Add or change programs in the
          Pricing panel.
        </p>
        {onOpenPricing ? (
          <Button size="sm" variant="outline" className="w-full" onClick={onOpenPricing}>
            Open Pricing
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-1.5 border-t pt-3">
      <Label>
        Items in <span className="font-mono">{over}</span>
      </Label>
      {items.length === 0 ? (
        <p className="text-[11px] text-muted-foreground">No items yet. Add one, then edit its fields on the page.</p>
      ) : (
        <ul className="space-y-1">
          {items.map((item, i) => {
            const label = repeatItemLabel(item, i);
            return (
              <li key={i} className="flex items-center gap-0.5 rounded-md border p-1 text-[12px]">
                <span className="min-w-0 flex-1 truncate px-1">{label}</span>
                <button
                  type="button"
                  aria-label={`Move ${label} up`}
                  disabled={i === 0}
                  className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                  onClick={() => {
                    const next = [...items];
                    [next[i - 1], next[i]] = [next[i], next[i - 1]];
                    setItems(next);
                  }}
                >
                  <ChevronUp className="size-3.5" />
                </button>
                <button
                  type="button"
                  aria-label={`Move ${label} down`}
                  disabled={i === items.length - 1}
                  className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                  onClick={() => {
                    const next = [...items];
                    [next[i], next[i + 1]] = [next[i + 1], next[i]];
                    setItems(next);
                  }}
                >
                  <ChevronDown className="size-3.5" />
                </button>
                <button
                  type="button"
                  aria-label={`Remove ${label}`}
                  className="text-muted-foreground hover:text-destructive"
                  onClick={() => setItems(items.filter((_, j) => j !== i))}
                >
                  <Trash2 className="size-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <Button size="sm" variant="outline" className="w-full" onClick={() => setItems([...items, blankRepeatItem(items)])}>
        <Plus className="size-3.5" /> Add item
      </Button>
    </div>
  );
}

/** Preserve the data's number-ness when a text control hands back a string. */
function castLike(previous: unknown, next: unknown): unknown {
  if (typeof previous === 'number' && typeof next === 'string' && next.trim() !== '') {
    const n = Number(next);
    if (!Number.isNaN(n)) return n;
  }
  return next;
}

/**
 * One prop row, binding-aware: a literal edits the block prop; a bound value
 * edits the DATA it points at (never a silent override), with a link chip
 * to inspect, change or unlink the binding.
 */
function FieldRow({ doc, blockId, suffix, field, raw, onProp, onData, dataPaths }: {
  doc: DocumentV3;
  blockId: string;
  suffix: string;
  field: FieldSpec;
  raw: unknown;
  onProp: (v: unknown) => void;
  onData: (path: string, v: unknown) => void;
  dataPaths: string[];
}) {
  const [editingLink, setEditingLink] = useState(false);
  const bound = isBindV3(raw);

  if (!bound) {
    return (
      <PropControl
        field={field}
        value={raw}
        onChange={onProp}
        action={
          <button
            type="button"
            className="text-muted-foreground hover:text-primary"
            title="Bind to data"
            onClick={() => {
              setEditingLink(true);
              onProp({ $bind: '' });
            }}
          >
            <Link2 className="size-3.5" />
          </button>
        }
      />
    );
  }

  const bind = raw as BindV3;
  const path = bind.$bind ? concreteBindPath(doc, blockId, bind.$bind, suffix) : '';
  const dataValue = path ? resolvePath(doc.data, path) : undefined;

  if (editingLink || !bind.$bind) {
    return (
      <div className="space-y-1">
        <Label className="flex min-h-4 items-center justify-between gap-2">
          <span className="truncate">{field.label}</span>
          <span className="flex items-center gap-1">
            {bind.$bind ? (
              <button
                type="button"
                className="text-muted-foreground hover:text-primary"
                title="Done"
                onClick={() => setEditingLink(false)}
              >
                <Check className="size-3.5" />
              </button>
            ) : null}
            <button
              type="button"
              className="text-muted-foreground hover:text-destructive"
              title="Unlink — copy the current value into the block"
              onClick={() => {
                setEditingLink(false);
                onProp(dataValue !== undefined ? structuredClone(dataValue) : '');
              }}
            >
              <Link2Off className="size-3.5" />
            </button>
          </span>
        </Label>
        <BindEditor value={bind} onChange={onProp} dataPaths={dataPaths} />
      </div>
    );
  }

  return (
    <PropControl
      field={field}
      value={dataValue}
      onChange={(v) => onData(path, castLike(dataValue, v))}
      action={
        <span className="flex max-w-[60%] items-center gap-1 text-primary" title={`Editing data at ${path}`}>
          <Link2 className="size-3.5 shrink-0" />
          <span className="truncate font-mono text-[10px] normal-case">{bind.$bind}</span>
          <button
            type="button"
            className="text-muted-foreground hover:text-primary"
            title="Change binding"
            onClick={() => setEditingLink(true)}
          >
            <Pencil className="size-3" />
          </button>
        </span>
      }
    />
  );
}

const ALIGN_OPTIONS = ['', 'start', 'center', 'end', 'stretch'] as const;
const JUSTIFY_OPTIONS = ['', 'start', 'center', 'end', 'between'] as const;

function StyleControl({ name, style, tokens, onPatch }: {
  name: StyleFieldKey;
  style: StyleOverrides;
  tokens: string[];
  onPatch: (patch: Partial<StyleOverrides>) => void;
}) {
  const value = style[name];
  switch (name) {
    case 'gap':
    case 'radius':
    case 'mt':
    case 'mb': {
      const NUMBER_LABELS: Partial<Record<StyleFieldKey, string>> = { mt: 'space above (px)', mb: 'space below (px)' };
      return (
        <div className="space-y-1">
          <Label>{NUMBER_LABELS[name] ?? name}</Label>
          <Input
            type="number"
            value={typeof value === 'number' ? value : ''}
            onChange={(e) =>
              onPatch({ [name]: e.target.value === '' ? undefined : Number(e.target.value) })
            }
          />
        </div>
      );
    }
    case 'align':
    case 'justify':
      return (
        <div className="space-y-1">
          <Label>{name}</Label>
          <NativeSelect
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onPatch({ [name]: e.target.value || undefined })}
          >
            {(name === 'align' ? ALIGN_OPTIONS : JUSTIFY_OPTIONS).map((o) => (
              <option key={o} value={o}>{o || '—'}</option>
            ))}
          </NativeSelect>
        </div>
      );
    case 'grow':
      return (
        <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          <input
            type="checkbox"
            checked={value === true}
            onChange={(e) => onPatch({ grow: e.target.checked || undefined })}
          />
          grow (fill space)
        </label>
      );
    case 'bg':
    case 'color':
      return (
        <div className="space-y-1">
          <Label>{name === 'bg' ? 'background' : 'color'}</Label>
          <Input
            list="docgen-color-tokens"
            value={typeof value === 'string' ? value : ''}
            placeholder="token or CSS color"
            onChange={(e) => onPatch({ [name]: e.target.value || undefined })}
          />
          <datalist id="docgen-color-tokens">
            {tokens.map((t) => (
              <option key={t} value={t} />
            ))}
          </datalist>
        </div>
      );
    default:
      return (
        <div className="space-y-1">
          <Label>{name}</Label>
          <Input
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onPatch({ [name]: e.target.value || undefined })}
          />
        </div>
      );
  }
}

/** A friendly alias for a picked list path: programs → program, items → item. */
function suggestAlias(over: string): string {
  const last = over.split('.').pop() ?? 'item';
  if (/s$/i.test(last) && last.length > 2) return last.slice(0, -1);
  return 'item';
}

function RepeatControls({ over, as, options, onChange }: {
  over: string | undefined;
  as: string | undefined;
  /** array paths available in this block's scope */
  options: string[];
  onChange: (repeat: { over: string; as: string } | undefined) => void;
}) {
  const known = over === undefined || options.includes(over);
  return (
    <div className="space-y-1.5">
      <Label>Repeat over list</Label>
      <div className="flex gap-1.5">
        <NativeSelect
          value={over ?? ''}
          aria-label="List to repeat over"
          onChange={(e) => {
            const v = e.target.value;
            onChange(v ? { over: v, as: suggestAlias(v) } : undefined);
          }}
        >
          <option value="">Don't repeat</option>
          {!known && over !== undefined ? <option value={over}>{over}</option> : null}
          {options.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </NativeSelect>
        {over !== undefined && (
          <Input
            aria-label="Item name"
            title="The name each item gets inside this repeat (used in bindings)"
            value={as ?? ''}
            className="w-24 font-mono text-xs"
            onChange={(e) => onChange({ over, as: e.target.value || 'item' })}
          />
        )}
      </div>
      {over !== undefined && (
        <p className="text-[10px] text-muted-foreground">
          Bind values inside with <span className="font-mono">{as || 'item'}.…</span>
        </p>
      )}
    </div>
  );
}

function Breadcrumb({ doc, blockId, onSelect }: {
  doc: DocumentV3;
  blockId: string;
  onSelect: InspectorProps['onSelect'];
}) {
  const ancestry = blockAncestry(doc, blockId);
  if (!ancestry) return null;
  const crumbs: { label: string; select: Selection }[] = [
    { label: ancestry.page.name ?? ancestry.page.id, select: { kind: 'page', id: ancestry.page.id } },
    ...ancestry.chain.map((node) => ({
      label: BLOCKS_BY_TYPE.get(node.type)?.name ?? node.type,
      select: { kind: 'block', id: node.id } as Selection,
    })),
  ];
  return (
    <nav className="flex flex-wrap items-center gap-0.5 text-[10px] text-muted-foreground">
      {crumbs.map((crumb, i) => (
        <span key={i} className="flex items-center gap-0.5">
          {i > 0 && <span>/</span>}
          <button
            type="button"
            className={cn('hover:text-primary', i === crumbs.length - 1 && 'font-semibold text-foreground')}
            onClick={() => onSelect(crumb.select)}
          >
            {crumb.label}
          </button>
        </span>
      ))}
    </nav>
  );
}

function BlockInspector({ doc, blockId, suffix, onChange, onSelect }: {
  doc: DocumentV3;
  blockId: string;
  suffix: string;
  onChange: InspectorProps['onChange'];
  onSelect: InspectorProps['onSelect'];
}) {
  const found = findBlock(doc, blockId);
  const dataPaths = useMemo(() => collectDataPaths(doc.data), [doc.data]);
  const repeatOptions = useMemo(() => {
    const out = collectArrayPaths(doc.data);
    if (!found) return out;
    // arrays reachable through the ancestors' repeat aliases (never our own)
    const frames = repeatFramesFor(doc, blockId, suffix);
    const scoped = found.block.repeat ? frames.slice(0, -1) : frames;
    scoped.forEach((frame, i) => {
      const concrete = concretizePath(frame.over, scoped.slice(0, i));
      const arr = resolvePath(doc.data, concrete);
      if (Array.isArray(arr) && arr.length > 0) {
        out.push(...collectArrayPaths(arr[0], frame.as));
      }
    });
    return out;
  }, [doc, blockId, suffix, found]);
  const tokens = Object.keys(doc.theme.colors ?? {});
  if (!found) return <p className="p-3 text-xs text-muted-foreground">Block not found.</p>;
  const def = BLOCKS_BY_TYPE.get(found.block.type);
  if (!def) return <p className="p-3 text-xs text-muted-foreground">Unknown block type.</p>;
  return (
    <div className="space-y-5 p-3">
      <Breadcrumb doc={doc} blockId={blockId} onSelect={onSelect} />
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">{def.name}</h3>
          <p className="text-[11px] text-muted-foreground">{def.description}</p>
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" aria-label="Duplicate block"
            onClick={() => onChange(duplicateBlock(doc, blockId))}>
            <Copy />
          </Button>
          <Button variant="ghost" size="icon" aria-label="Delete block"
            onClick={() => {
              onChange(removeBlock(doc, blockId));
              onSelect(null);
            }}>
            <Trash2 />
          </Button>
        </div>
      </div>

      {def.propFields.length > 0 && (
        <section className="space-y-3">
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Content</h4>
          {def.propFields.map((field) => (
            <FieldRow
              key={field.name}
              doc={doc}
              blockId={blockId}
              suffix={suffix}
              field={field}
              raw={found.block.props[field.name]}
              dataPaths={dataPaths}
              onProp={(v) => onChange(setBlockProp(doc, blockId, field.name, v), `prop:${blockId}:${field.name}`)}
              onData={(path, v) => onChange(setDocData(doc, path, v), `data:${path}`)}
            />
          ))}
        </section>
      )}

      {def.styleFields.length > 0 && (
        <section className="space-y-3">
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Style</h4>
          {def.styleFields.map((name) => (
            <StyleControl
              key={name}
              name={name}
              style={found.block.style ?? {}}
              tokens={tokens}
              onPatch={(patch) => onChange(setBlockStyle(doc, blockId, patch), `style:${blockId}`)}
            />
          ))}
        </section>
      )}

      <section className="space-y-2">
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Data</h4>
        <RepeatControls
          over={found.block.repeat?.over}
          as={found.block.repeat?.as}
          options={repeatOptions}
          onChange={(repeat) =>
            onChange(updateBlock(doc, blockId, (b) => ({ ...b, repeat })), `repeat:${blockId}`)
          }
        />
      </section>
    </div>
  );
}

function PageInspector({ doc, pageId, onChange, onSelect, onOpenPricing }: {
  doc: DocumentV3;
  pageId: string;
  onChange: InspectorProps['onChange'];
  onSelect: InspectorProps['onSelect'];
  onOpenPricing?: (() => void) | undefined;
}) {
  const page = doc.pages.find((p) => p.id === pageId);
  const tokens = Object.keys(doc.theme.colors ?? {});
  if (!page) return <p className="p-3 text-xs text-muted-foreground">Page not found.</p>;
  const set = (fn: (p: PageV3) => PageV3, key?: string) => onChange(updatePage(doc, pageId, fn), key);
  return (
    <div className="space-y-4 p-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Page</h3>
          <p className="text-[11px] text-muted-foreground">{page.name ?? page.id}</p>
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" aria-label="Duplicate page"
            onClick={() => onChange(duplicatePage(doc, pageId))}>
            <Copy />
          </Button>
          <Button variant="ghost" size="icon" aria-label="Delete page"
            onClick={() => {
              onChange(removePage(doc, pageId));
              onSelect(null);
            }}>
            <Trash2 />
          </Button>
        </div>
      </div>
      <div className="space-y-1">
        <Label>Name</Label>
        <Input
          value={page.name ?? ''}
          onChange={(e) => set((p) => ({ ...p, name: e.target.value || undefined }), `pgname:${pageId}`)}
        />
      </div>
      <div className="space-y-1">
        <Label>Background</Label>
        <Input
          list="docgen-color-tokens"
          value={page.background ?? ''}
          placeholder="token or CSS color"
          onChange={(e) => set((p) => ({ ...p, background: e.target.value || undefined }), `pgbg:${pageId}`)}
        />
        <datalist id="docgen-color-tokens">
          {tokens.map((t) => (
            <option key={t} value={t} />
          ))}
        </datalist>
      </div>
      <div className="space-y-1">
        <Label>Padding</Label>
        <Input
          value={page.pad ?? ''}
          placeholder="e.g. 0.72in 0.78in"
          onChange={(e) => set((p) => ({ ...p, pad: e.target.value || undefined }), `pgpad:${pageId}`)}
        />
      </div>
      <RepeatControls
        over={page.repeat?.over}
        as={page.repeat?.as}
        options={collectArrayPaths(doc.data)}
        onChange={(repeat) => set((p) => ({ ...p, repeat }), `pgrepeat:${pageId}`)}
      />
      {page.repeat?.over ? (
        <RepeatItemsEditor doc={doc} over={page.repeat.over} onChange={onChange} onOpenPricing={onOpenPricing} />
      ) : null}
    </div>
  );
}

function DocInspector({ doc, onChange }: {
  doc: DocumentV3;
  onChange: InspectorProps['onChange'];
}) {
  const [dataDraft, setDataDraft] = useState<string | null>(null);
  const [dataError, setDataError] = useState<string | null>(null);
  return (
    <div className="space-y-4 p-3">
      <h3 className="text-sm font-semibold">Document</h3>
      <div className="space-y-1">
        <Label>Name</Label>
        <Input value={doc.name} onChange={(e) => onChange({ ...doc, name: e.target.value }, 'docname')} />
      </div>
      <div className="space-y-1">
        <Label>Data (JSON)</Label>
        <Textarea
          rows={16}
          className="font-mono text-[11px]"
          value={dataDraft ?? JSON.stringify(doc.data, null, 2)}
          onChange={(e) => setDataDraft(e.target.value)}
          spellCheck={false}
        />
        {dataDraft !== null && (
          <div className="flex items-center gap-2">
            <Button size="sm"
              onClick={() => {
                try {
                  const data = documentV3Schema.shape.data.parse(JSON.parse(dataDraft));
                  onChange({ ...doc, data });
                  setDataDraft(null);
                  setDataError(null);
                } catch (e) {
                  setDataError(e instanceof Error ? e.message.slice(0, 120) : 'invalid JSON');
                }
              }}>
              Apply
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setDataDraft(null); setDataError(null); }}>
              Discard
            </Button>
          </div>
        )}
        {dataError && <p className="text-[11px] text-destructive">{dataError}</p>}
      </div>
    </div>
  );
}

/** The right tool pane: edits the selected block, page, or the document. */
export function Inspector({ doc, selection, onChange, onSelect, onOpenPricing }: InspectorProps) {
  if (selection?.kind === 'block') {
    const suffix = selection.key?.startsWith(selection.id)
      ? selection.key.slice(selection.id.length)
      : '';
    return (
      <BlockInspector
        doc={doc}
        blockId={selection.id}
        suffix={suffix}
        onChange={onChange}
        onSelect={onSelect}
      />
    );
  }
  if (selection?.kind === 'page') {
    return (
      <PageInspector doc={doc} pageId={selection.id} onChange={onChange} onSelect={onSelect} onOpenPricing={onOpenPricing} />
    );
  }
  return <DocInspector doc={doc} onChange={onChange} />;
}
